import * as XLSX from "xlsx";

(() => {
  const MARKER = "SIP_WO_CENTER_EXPORT_EXCEL_STAGING_V1";
  const EXPORT_ID = "sip-wo-export-excel-v1";
  let queued = false;

  const norm = (v) => String(v || "").replace(/\s+/g, " ").trim();
  const upper = (v) => norm(v).toUpperCase();

  function visible(el) {
    if (!(el instanceof HTMLElement)) return false;
    const cs = getComputedStyle(el);
    if (
      cs.display === "none" ||
      cs.visibility === "hidden" ||
      Number(cs.opacity || 1) === 0
    ) return false;

    const r = el.getBoundingClientRect();
    return r.width > 1 && r.height > 1;
  }

  function isWoPage() {
    const t = upper(document.body?.innerText || "");
    return (
      t.includes("APLIKASI WO ENGINEERING") &&
      t.includes("WORK ORDER")
    );
  }

  function findTotalWoLabel() {
    const nodes = [...document.querySelectorAll("body *")]
      .filter((el) => {
        if (!(el instanceof HTMLElement) || !visible(el)) return false;
        const t = upper(el.textContent);
        if (!/^\d+\s*TOTAL\s*WO$/i.test(t) || t.length > 80) return false;

        return ![...el.children].some((c) =>
          /^\d+\s*TOTAL\s*WO$/i.test(upper(c.textContent))
        );
      });

    return nodes
      .map((el) => {
        const r = el.getBoundingClientRect();
        return { el, area: Math.max(1, r.width * r.height) };
      })
      .sort((a, b) => a.area - b.area)[0]?.el || null;
  }

  function findChart(label) {
    let el = label;

    for (let depth = 0; el && depth < 8; depth += 1, el = el.parentElement) {
      if (!(el instanceof HTMLElement)) continue;
      const r = el.getBoundingClientRect();
      const cls = upper(el.className);

      const hasGraphic =
        !!el.querySelector("svg,canvas") ||
        cls.includes("DONUT") ||
        cls.includes("PIE") ||
        cls.includes("CHART");

      const sizeOk =
        r.width >= 130 &&
        r.height >= 130 &&
        r.width <= 500 &&
        r.height <= 500;

      const squareish =
        r.height > 0 &&
        r.width / r.height >= 0.6 &&
        r.width / r.height <= 1.8;

      if (sizeOk && squareish && hasGraphic) return el;
    }

    return null;
  }

  function centerTotalWo() {
    const label = findTotalWoLabel();
    if (!label) return;

    const chart = findChart(label);
    if (!chart) return;

    if (getComputedStyle(chart).position === "static") {
      chart.style.position = "relative";
    }

    Object.assign(label.style, {
      position: "absolute",
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
      margin: "0",
      padding: "0",
      width: "96px",
      maxWidth: "96px",
      textAlign: "center",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      lineHeight: "1.08",
      zIndex: "5",
      pointerEvents: "none"
    });

    [...label.querySelectorAll("*")].forEach((child) => {
      if (!(child instanceof HTMLElement)) return;
      child.style.textAlign = "center";
      child.style.marginLeft = "auto";
      child.style.marginRight = "auto";
    });
  }

  function findPrintButton() {
    return [...document.querySelectorAll("button,a,[role='button']")]
      .find((el) => {
        if (!(el instanceof HTMLElement) || !visible(el)) return false;
        const t = upper(el.innerText || el.textContent);

        return (
          t === "PRINT" ||
          t === "CETAK" ||
          t.includes("PRINT LAPORAN") ||
          t.includes("CETAK LAPORAN")
        );
      }) || null;
  }

  function safeSheetName(name, fallback) {
    return (
      String(name || fallback || "Sheet")
        .replace(/[\\/?*\[\]:]/g, " ")
        .trim()
        .slice(0, 31) || fallback || "Sheet"
    );
  }

  function exportExcel(printBtn) {
    try {
      const wb = XLSX.utils.book_new();

      const visibleTables = [...document.querySelectorAll("table")]
        .filter((table) => visible(table));

      let count = 0;

      visibleTables.forEach((table, index) => {
        const ws = XLSX.utils.table_to_sheet(table, { raw: true });

        const heading =
          table.closest("section,div")
            ?.querySelector("h1,h2,h3,h4,h5,strong")
            ?.textContent || "";

        XLSX.utils.book_append_sheet(
          wb,
          ws,
          safeSheetName(heading, `Laporan ${index + 1}`)
        );

        count += 1;
      });

      if (!count) {
        window.alert(
          "Data laporan WO belum tampil atau belum ada data yang dapat diexport."
        );
        return;
      }

      const d = new Date();
      const stamp = [
        d.getFullYear(),
        String(d.getMonth() + 1).padStart(2, "0"),
        String(d.getDate()).padStart(2, "0")
      ].join("-");

      XLSX.writeFile(
        wb,
        `Laporan_WO_Engineering_${stamp}.xlsx`
      );
    } catch (err) {
      console.error(`[${MARKER}] export error`, err);
      window.alert("Export Excel gagal. Refresh halaman lalu coba kembali.");
    }
  }

  function installExportButton() {
    if (document.getElementById(EXPORT_ID)) return;

    const printBtn = findPrintButton();
    if (!printBtn || !printBtn.parentElement) return;

    const btn = document.createElement("button");

    btn.id = EXPORT_ID;
    btn.type = "button";
    btn.className = printBtn.className || "";
    btn.textContent = "Export Excel";
    btn.title = "Export laporan WO ke Excel";
    btn.style.marginLeft = "8px";
    btn.style.whiteSpace = "nowrap";

    btn.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      exportExcel(printBtn);
    });

    printBtn.insertAdjacentElement("afterend", btn);
  }

  function apply() {
    if (!isWoPage()) return;
    centerTotalWo();
    installExportButton();
  }

  function queue() {
    if (queued) return;
    queued = true;

    setTimeout(() => {
      queued = false;
      apply();
    }, 80);
  }

  function start() {
    apply();

    new MutationObserver(queue).observe(
      document.documentElement,
      {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "style"]
      }
    );

    window.addEventListener("popstate", queue);
    window.addEventListener("resize", queue);

    [100, 300, 700, 1300].forEach((ms) => setTimeout(queue, ms));

    console.log(`[${MARKER}] installed`);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();

if (typeof window !== "undefined") {
  window.__SIP_WO_EXPORT_EXCEL_PRODUCTION_V2__ = "SIP_WO_EXPORT_EXCEL_PRODUCTION_V2";
}
